<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'Payment',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    oid: {
      type: String,
      required: false,
      default: null,
    },
    invoices: {
      type: Array,
      required: false,
      default: null,
    },
    accountId: {
      type: String,
      required: false,
    },
    transactionKind: {
      type: String,
      required: false,
      default: null,
    },
    transactionType: {
      type: String,
      required: false,
      default: null,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const recalculateAmountFromLines = (...args) => {
    const _item = item.value;

    if (_item.isReadonly) {
      return;
    }

    const [invoiceData] = args;

    if (invoiceData) {
      const idx = _.findIndex(_item.invoices.data, {
        invoice_id: invoiceData.invoice_id,
      });
      if (idx > -1) {
        _.extend(_item.invoices.data[idx], invoiceData);
      }
    }

    if (!autocalcAmount.value) {
      return;
    }

    const summa = $fn.value.numeral(0);

    if (!_item.invoices || !_item.invoices.data) {
      return;
    }

    for (let d of _item.invoices.data) {
      if (d.amount == null) {
        continue;
      }
      summa.add(d.amount);
    }

    _item.amount = summa.value();

    if (isPettyCashPayment.value) {
      _item.amount = Math.abs(_item.amount);
    }
  };
  const reloadTemplate = (...args) => {
    loadData('item');
  };
  const addPendingAttachment = (...args) => {
    const [payload] = args;
    if (!payload || !payload.file) {
      return;
    }
    pendingAttachments.value = [
      ...pendingAttachments.value,
      {
        _uid: $fn.value.randomElementId(),
        file: payload.file,
        description: payload.description || null,
      },
    ];
  };
  const removePendingAttachment = (...args) => {
    const [uid] = args;
    pendingAttachments.value = pendingAttachments.value.filter(
      (p) => p._uid !== uid
    );
  };
  const uploadPendingAttachments = (...args) => {
    const pending = pendingAttachments.value;
    const ownerId = item.value && item.value.id;

    if (!ownerId || !pending || !pending.length) {
      return;
    }

    return Promise.all(
      pending.map((p) =>
        $fn.value.fetch('create_attachment', {
          contentType: 'multipart/form-data',
          content: {
            object_id: ownerId,
            description: p.description,
            file: p.file,
          },
        })
      )
    ).then(() => {
      pendingAttachments.value = [];
      attachmentsReload.value = Date.now();
    });
  };

  const formId = ref(null);
  const pendingReload = ref(false);
  const pendingAttachments = ref([]);
  const attachmentsReload = ref(0);
  const autocalcAmount = ref(true);
  const item = ref(null);
  const allConfig = ref({
    transactionTypes: {
      bank: {
        credit: $fn.value.pgettext('TransactionType', 'credit'),
        debit: $fn.value.pgettext('TransactionType', 'debit'),
      },
      cash_book: {
        deposit: $fn.value.pgettext('TransactionType', 'deposit'),
        payment: $fn.value.pgettext('TransactionType', 'payment'),
      },
      technical: {
        invoice_reconciliation: $fn.value.pgettext(
          'TransactionType',
          'invoice reconciliation'
        ),
        write_off: $fn.value.pgettext('TransactionType', 'write off'),
      },
    },
    bank: {
      credit: {
        fields: {},
        resourceTitle: $fn.value.pgettext('Payment|Header', 'Bank credit'),
        newTransaction: $fn.value.pgettext('Payment|Header', 'New transaction'),
      },
      debit: {
        fields: {},
        resourceTitle: $fn.value.pgettext('Payment|Header', 'Bank debit'),
        newTransaction: $fn.value.pgettext('Payment|Header', 'New transaction'),
      },
    },
    cash_book: {
      deposit: {
        fields: {
          transaction_number: false,
          transaction_type: 'ro',
          exchange_rate: false,
          statement_number: false,
          transaction_code: false,
        },
        resourceTitle: $fn.value.pgettext('Payment|Header', 'Cash-In'),
        newTransaction: $fn.value.pgettext('Payment|Header', 'New transaction'),
      },
      payment: {
        fields: {
          transaction_number: false,
          transaction_type: 'ro',
          exchange_rate: false,
        },
        resourceTitle: $fn.value.pgettext('Payment|Header', 'Cash-Out'),
        newTransaction: $fn.value.pgettext('Payment|Header', 'New transaction'),
      },
    },
    technical: {
      invoice_reconciliation: {
        fields: {
          amount: false,
          exchange_rate: false,
          transaction_number: false,
          payment_method: false,
          employee: false,
          statement_number: false,
          transaction_code: false,
        },
        resourceTitle: $fn.value.pgettext(
          'Payment|Header',
          'Invoice reconciliation'
        ),
        newTransaction: $fn.value.pgettext('Payment|Header', 'New transaction'),
      },
      write_off: {
        fields: {
          amount: false,
          exchange_rate: false,
          transaction_number: false,
          payment_method: false,
          employee: false,
          statement_number: false,
          transaction_code: false,
        },
        resourceTitle: $fn.value.pgettext('Payment|Header', 'Write off'),
        newTransaction: $fn.value.pgettext('Payment|Header', 'New transaction'),
      },
    },
  });
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));

  const $data = computed(() => ({
    formId: formId.value,
    pendingReload: pendingReload.value,
    pendingAttachments: pendingAttachments.value,
    attachmentsReload: attachmentsReload.value,
    autocalcAmount: autocalcAmount.value,
    item: item.value,
    allConfig: allConfig.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_item = async () => {
    return props.oid == null
      ? $fn.value.fetch('get_payment_template', {
          asResource: true,
          parameters: {
            kind: props.transactionKind,
            type: props.transactionType,
            invoices: props.invoices ? props.invoices.join(',') : null,
            account_id:
              ($data.value.item &&
                $data.value.item.account &&
                $data.value.item.account.id) ||
              props.accountId,
          },
        })
      : $fn.value.fetch('read_payment', {
          asResource: true,
          parameters: { id: props.oid },
        });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'pendingReload' },
      { name: 'pendingAttachments' },
      { name: 'attachmentsReload' },
      {
        name: 'autocalcAmount',
        cache: { type: 'local', key: 'PaymentInvoices.autocalcAmount' },
      },
      { name: 'item' },
      { name: 'allConfig' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { formId: $$load_formId, item: $$load_item },
    ux: {},
    dataRefs: {
      formId,
      pendingReload,
      pendingAttachments,
      attachmentsReload,
      autocalcAmount,
      item,
      allConfig,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const isDeletable = computed(() => {
    return props.oid != null;
  });
  const isNew = computed(() => {
    if (loaded.value) {
      return item.value && item.value.id == null;
    }
    return null;
  });
  const isPettyCashPayment = computed(() => {
    if (loaded.value) {
      return (
        item.value.account && item.value.account.account_type === 'cash_book'
      );
    }
    return null;
  });
  const config = computed(() => {
    if (loaded.value) {
      return allConfig.value[item.value.transaction_kind][
        item.value.transaction_type
      ];
    }
    return null;
  });
  const transactionTypes = computed(() => {
    if (loaded.value) {
      return allConfig.value.transactionTypes[item.value.transaction_kind];
    }
    return null;
  });
  const pageDocument = computed(() => {
    return { title: config.value ? config.value.resourceTitle : '' };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.oid,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('oid');
    }
  );
  watch(
    () => props.invoices,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoices');
    }
  );
  watch(
    () => props.accountId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('accountId');
    }
  );
  watch(
    () => props.transactionKind,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('transactionKind');
    }
  );
  watch(
    () => props.transactionType,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('transactionType');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_7t33g881v_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
        ok: pendingReload.value,
      },
    };
  };
  const act_8564662037462133_closeModal_pecehu4ne_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
        ok: pendingReload.value,
      },
    };
  };
  const act_6466705138206980_closeModal_7kqtz4lv9_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
        ok: true,
      },
    };
  };
  const act_6466705138206980_reloadSlickgrid_ro357k54q_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'payments',
    };
  };
  const act_1040167445267876_setData_02z9al7qv_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item',
      value: $event && $event.data,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_1040167445267876_setData_02z9al7qv_getWhen = async ($event) => {
    return !!$event.data;
  };
  const act_1040167445267876_script_7su4nbs9p_value_callable = async (
    $event
  ) => {
    return uploadPendingAttachments();
  };
  const act_1040167445267876_script_7su4nbs9p_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_1040167445267876_script_7su4nbs9p_value_callable($event);
      },
    };
  };
  const act_1040167445267876_script_7su4nbs9p_getWhen = async ($event) => {
    return !!$event.data;
  };
  const act_1040167445267876_setData_film50t8a_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'pendingReload',
      value: true,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_1040167445267876_setData_film50t8a_getWhen = async ($event) => {
    return !!$event.data;
  };
  const act_1040167445267876_reloadSlickgrid_ogirlamoi_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'payments',
    };
  };
  const act_1040167445267876_reloadSlickgrid_ogirlamoi_getWhen = async (
    $event
  ) => {
    return !!$event.data;
  };
  const act_1040167445267876_crud_ldqor5ezz_getActionArgs = async ($event) => {
    return {
      objectType: 'payment',
      op: 'upsert',
      data: item.value,
    };
  };
  const act_5534025912102772_script_4d8cccxzr_value_callable = async (
    $event
  ) => {
    return reloadTemplate($event.data);
  };
  const act_5534025912102772_script_4d8cccxzr_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5534025912102772_script_4d8cccxzr_value_callable($event);
      },
    };
  };
  const act_5534025912102772_setData_hicowvku1_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'autocalcAmount',
      value: $event.originalEvent,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_6802360265496969_script_tu58hlx5n_value_callable = async (
    $event
  ) => {
    return addPendingAttachment($event.data);
  };
  const act_6802360265496969_script_tu58hlx5n_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_6802360265496969_script_tu58hlx5n_value_callable($event);
      },
    };
  };
  const act_6802360265496969_script_wypx1is9n_value_callable = async (
    $event
  ) => {
    return removePendingAttachment($event.data);
  };
  const act_6802360265496969_script_wypx1is9n_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_6802360265496969_script_wypx1is9n_value_callable($event);
      },
    };
  };
  const act_3083451129821185_script_ov3yc1kfb_value_callable = async (
    $event
  ) => {
    return recalculateAmountFromLines($event.data);
  };
  const act_3083451129821185_script_ov3yc1kfb_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3083451129821185_script_ov3yc1kfb_value_callable($event);
      },
    };
  };
  const act_2434514588044777_setData_sb3hdplvi_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item',
      value: $event && $event.data,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const inst_evh_2434514588044777_success_mgpu74ha7_getWhen = async (
    $event
  ) => {
    return !!$event.data;
  };
  const act_2434514588044777_setData_kqc5rm7c9_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'pendingReload',
      value: true,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const inst_evh_2434514588044777_success_9n9f0lebf_getWhen = async (
    $event
  ) => {
    return !!$event.data;
  };
  const act_2434514588044777_reloadSlickgrid_27uojh7pu_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'payments',
    };
  };
  const inst_evh_2434514588044777_success_jla2edxfx_getWhen = async (
    $event
  ) => {
    return !!$event.data;
  };
  const act_2434514588044777_rest_wczh6k8vz_getActionArgs = async ($event) => {
    return {
      method: 'save',
      container: {
        self: item.value.links.parent,
        object: 'list',
        data: [],
      },
      resource: item.value,
      data: {
        etag: item.value.etag,
        complete: true,
      },
    };
  };
  const act_2434514588044777_rest_wczh6k8vz_getConfirm = async ($event) => {
    return {
      header: config.value ? config.value.resourceTitle : '',
      message: $fn.value.gettext(
        'Completing prevents further modifications. Please confirm this operation.'
      ),
      btnOk: $fn.value.gettext('Complete'),
      severity: 'warning',
    };
  };
  const act_7253299514750264_setData_6uj93zxt2_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item',
      value: item.value.clone({
        id: null,
        rev: null,
        transaction_number: isPettyCashPayment.value
          ? null
          : item.value.transaction_number,
        amount: 0,
        links: null,
        etag: null,
        invoices: { data: [] },
        preview_url: null,
        locked: false,
        notes: null,
        internal_notes: null,
      }),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_7253299514750264_setData_wb4rynhoz_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'pendingAttachments',
      value: [],
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };

  const act_7315092382398562_closeModal_7t33g881v =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_7t33g881v_getActionArgs,
    });
  const inst_evh_7315092382398562_close_bgvz0h7a7 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_7t33g881v,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_bgvz0h7a7],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_01tc143z3 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_tsht8km27 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_01tc143z3,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_tsht8km27],
    event: 'reload',
  });
  const act_8564662037462133_closeModal_pecehu4ne =
    new actionTypes.CloseModalAction({
      actionArgs: act_8564662037462133_closeModal_pecehu4ne_getActionArgs,
    });
  const inst_evh_8564662037462133_close_eph2hrn0m =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeModal_pecehu4ne,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_eph2hrn0m],
    event: 'close',
  });
  const act_6466705138206980_reloadComponentData_zwtzezxbc =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_6466705138206980_reload_fx81151hw =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_reloadComponentData_zwtzezxbc,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_6466705138206980_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6466705138206980_reload_fx81151hw],
    event: 'reload',
  });
  const act_6466705138206980_closeModal_7kqtz4lv9 =
    new actionTypes.CloseModalAction({
      actionArgs: act_6466705138206980_closeModal_7kqtz4lv9_getActionArgs,
    });
  const inst_evh_6466705138206980_deleted_ozhdg60sq =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_closeModal_7kqtz4lv9,
      event: 'deleted',
      displayName: 'closeModal',
    });
  const act_6466705138206980_reloadSlickgrid_ro357k54q =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_6466705138206980_reloadSlickgrid_ro357k54q_getActionArgs,
    });
  const inst_evh_6466705138206980_deleted_xrwz8a1qu =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_reloadSlickgrid_ro357k54q,
      event: 'deleted',
      displayName: 'reloadSlickgrid',
    });
  const inst_evh_6466705138206980_deleted = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_6466705138206980_deleted_ozhdg60sq,
      inst_evh_6466705138206980_deleted_xrwz8a1qu,
    ],
    event: 'deleted',
  });
  const act_1040167445267876_setData_02z9al7qv = new actionTypes.SetDataAction({
    actionArgs: act_1040167445267876_setData_02z9al7qv_getActionArgs,
    when: act_1040167445267876_setData_02z9al7qv_getWhen,
  });
  const inst_evh_1040167445267876_success_5zplp50dl =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_setData_02z9al7qv,
      event: 'success',
      displayName: 'setData',
    });
  const act_1040167445267876_script_7su4nbs9p = new actionTypes.ScriptAction({
    actionArgs: act_1040167445267876_script_7su4nbs9p_getActionArgs,
    when: act_1040167445267876_script_7su4nbs9p_getWhen,
  });
  const inst_evh_1040167445267876_success_ik75uw7ly =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_script_7su4nbs9p,
      event: 'success',
      displayName: 'script',
    });
  const act_1040167445267876_setData_film50t8a = new actionTypes.SetDataAction({
    actionArgs: act_1040167445267876_setData_film50t8a_getActionArgs,
    when: act_1040167445267876_setData_film50t8a_getWhen,
  });
  const inst_evh_1040167445267876_success_b5wuheq22 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_setData_film50t8a,
      event: 'success',
      displayName: 'setData',
    });
  const act_1040167445267876_reloadSlickgrid_ogirlamoi =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_1040167445267876_reloadSlickgrid_ogirlamoi_getActionArgs,
      when: act_1040167445267876_reloadSlickgrid_ogirlamoi_getWhen,
    });
  const inst_evh_1040167445267876_success_ab4udnvvy =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_reloadSlickgrid_ogirlamoi,
      event: 'success',
      displayName: 'reloadSlickgrid',
    });
  const act_1040167445267876_crud_ldqor5ezz = new actionTypes.CRUDAction({
    actionArgs: act_1040167445267876_crud_ldqor5ezz_getActionArgs,
    displayName: 'save',
    events: [
      inst_evh_1040167445267876_success_5zplp50dl,
      inst_evh_1040167445267876_success_ik75uw7ly,
      inst_evh_1040167445267876_success_b5wuheq22,
      inst_evh_1040167445267876_success_ab4udnvvy,
    ],
  });
  const inst_evh_1040167445267876_submit_d3cs9xosk =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_crud_ldqor5ezz,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_d3cs9xosk],
    event: 'submit',
  });
  const act_5534025912102772_script_4d8cccxzr = new actionTypes.ScriptAction({
    actionArgs: act_5534025912102772_script_4d8cccxzr_getActionArgs,
  });
  const inst_evh_5534025912102772_accountChanged_sb1n04qqw =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_script_4d8cccxzr,
      event: 'accountChanged',
      displayName: 'script',
    });
  const inst_evh_5534025912102772_accountChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5534025912102772_accountChanged_sb1n04qqw],
      event: 'accountChanged',
    });
  const act_5534025912102772_setData_hicowvku1 = new actionTypes.SetDataAction({
    actionArgs: act_5534025912102772_setData_hicowvku1_getActionArgs,
  });
  const inst_evh_5534025912102772_autocalcAmountChanged_wmi30x8hz =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_setData_hicowvku1,
      event: 'autocalcAmountChanged',
      displayName: 'setData',
    });
  const inst_evh_5534025912102772_autocalcAmountChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5534025912102772_autocalcAmountChanged_wmi30x8hz],
      event: 'autocalcAmountChanged',
    });
  const act_6802360265496969_script_tu58hlx5n = new actionTypes.ScriptAction({
    actionArgs: act_6802360265496969_script_tu58hlx5n_getActionArgs,
  });
  const inst_evh_6802360265496969_stage_azdj3pcjt =
    new actionTypes.EventHandlerImpl({
      action: act_6802360265496969_script_tu58hlx5n,
      event: 'stage',
      displayName: 'script',
    });
  const inst_evh_6802360265496969_stage = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6802360265496969_stage_azdj3pcjt],
    event: 'stage',
  });
  const act_6802360265496969_script_wypx1is9n = new actionTypes.ScriptAction({
    actionArgs: act_6802360265496969_script_wypx1is9n_getActionArgs,
  });
  const inst_evh_6802360265496969_unstage_ueltguzbh =
    new actionTypes.EventHandlerImpl({
      action: act_6802360265496969_script_wypx1is9n,
      event: 'unstage',
      displayName: 'script',
    });
  const inst_evh_6802360265496969_unstage = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6802360265496969_unstage_ueltguzbh],
    event: 'unstage',
  });
  const act_3083451129821185_script_ov3yc1kfb = new actionTypes.ScriptAction({
    actionArgs: act_3083451129821185_script_ov3yc1kfb_getActionArgs,
  });
  const inst_evh_3083451129821185_recalculate_yvqdp52d3 =
    new actionTypes.EventHandlerImpl({
      action: act_3083451129821185_script_ov3yc1kfb,
      event: 'recalculate',
      displayName: 'script',
    });
  const inst_evh_3083451129821185_recalculate =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3083451129821185_recalculate_yvqdp52d3],
      event: 'recalculate',
    });
  const act_2434514588044777_setData_sb3hdplvi = new actionTypes.SetDataAction({
    actionArgs: act_2434514588044777_setData_sb3hdplvi_getActionArgs,
  });
  const inst_evh_2434514588044777_success_mgpu74ha7 =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_setData_sb3hdplvi,
      event: 'success',
      when: inst_evh_2434514588044777_success_mgpu74ha7_getWhen,
      displayName: 'setData',
    });
  const act_2434514588044777_setData_kqc5rm7c9 = new actionTypes.SetDataAction({
    actionArgs: act_2434514588044777_setData_kqc5rm7c9_getActionArgs,
  });
  const inst_evh_2434514588044777_success_9n9f0lebf =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_setData_kqc5rm7c9,
      event: 'success',
      when: inst_evh_2434514588044777_success_9n9f0lebf_getWhen,
      displayName: 'setData',
    });
  const act_2434514588044777_reloadSlickgrid_27uojh7pu =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_2434514588044777_reloadSlickgrid_27uojh7pu_getActionArgs,
    });
  const inst_evh_2434514588044777_success_jla2edxfx =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_reloadSlickgrid_27uojh7pu,
      event: 'success',
      when: inst_evh_2434514588044777_success_jla2edxfx_getWhen,
      displayName: 'reloadSlickgrid',
    });
  const act_2434514588044777_rest_wczh6k8vz = new actionTypes.RestAction({
    actionArgs: act_2434514588044777_rest_wczh6k8vz_getActionArgs,
    confirm: act_2434514588044777_rest_wczh6k8vz_getConfirm,
    displayName: 'complete',
    events: [
      inst_evh_2434514588044777_success_mgpu74ha7,
      inst_evh_2434514588044777_success_9n9f0lebf,
      inst_evh_2434514588044777_success_jla2edxfx,
    ],
  });
  const inst_evh_2434514588044777_clickPreventStop_5bchkyrft =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_rest_wczh6k8vz,
      event: 'click.prevent.stop',
      displayName: 'complete',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_5bchkyrft],
      event: 'click.prevent.stop',
    });
  const act_7253299514750264_setData_6uj93zxt2 = new actionTypes.SetDataAction({
    actionArgs: act_7253299514750264_setData_6uj93zxt2_getActionArgs,
  });
  const inst_evh_7253299514750264_clickPreventStop_izm99m383 =
    new actionTypes.EventHandlerImpl({
      action: act_7253299514750264_setData_6uj93zxt2,
      event: 'click.prevent.stop',
      displayName: 'setData',
    });
  const act_7253299514750264_setData_wb4rynhoz = new actionTypes.SetDataAction({
    actionArgs: act_7253299514750264_setData_wb4rynhoz_getActionArgs,
  });
  const inst_evh_7253299514750264_clickPreventStop_6w9qlxo6j =
    new actionTypes.EventHandlerImpl({
      action: act_7253299514750264_setData_wb4rynhoz,
      event: 'click.prevent.stop',
      displayName: 'setData',
    });
  const inst_evh_7253299514750264_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [
        inst_evh_7253299514750264_clickPreventStop_izm99m383,
        inst_evh_7253299514750264_clickPreventStop_6w9qlxo6j,
      ],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_bgvz0h7a7,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_tsht8km27,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_eph2hrn0m,
    inst_evh_8564662037462133_close,
    inst_evh_6466705138206980_reload_fx81151hw,
    inst_evh_6466705138206980_reload,
    inst_evh_6466705138206980_deleted_ozhdg60sq,
    inst_evh_6466705138206980_deleted_xrwz8a1qu,
    inst_evh_6466705138206980_deleted,
    inst_evh_1040167445267876_success_5zplp50dl,
    inst_evh_1040167445267876_success_ik75uw7ly,
    inst_evh_1040167445267876_success_b5wuheq22,
    inst_evh_1040167445267876_success_ab4udnvvy,
    inst_evh_1040167445267876_submit_d3cs9xosk,
    save: inst_evh_1040167445267876_submit_d3cs9xosk,
    inst_evh_1040167445267876_submit,
    inst_evh_5534025912102772_accountChanged_sb1n04qqw,
    inst_evh_5534025912102772_accountChanged,
    inst_evh_5534025912102772_autocalcAmountChanged_wmi30x8hz,
    inst_evh_5534025912102772_autocalcAmountChanged,
    inst_evh_6802360265496969_stage_azdj3pcjt,
    inst_evh_6802360265496969_stage,
    inst_evh_6802360265496969_unstage_ueltguzbh,
    inst_evh_6802360265496969_unstage,
    inst_evh_3083451129821185_recalculate_yvqdp52d3,
    inst_evh_3083451129821185_recalculate,
    inst_evh_2434514588044777_success_mgpu74ha7,
    inst_evh_2434514588044777_success_9n9f0lebf,
    inst_evh_2434514588044777_success_jla2edxfx,
    inst_evh_2434514588044777_clickPreventStop_5bchkyrft,
    complete: inst_evh_2434514588044777_clickPreventStop_5bchkyrft,
    inst_evh_2434514588044777_clickPreventStop,
    inst_evh_7253299514750264_clickPreventStop_izm99m383,
    inst_evh_7253299514750264_clickPreventStop_6w9qlxo6j,
    inst_evh_7253299514750264_clickPreventStop,
  };

  const evh_7315092382398562_close_bgvz0h7a7 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_bgvz0h7a7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_tsht8km27 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_tsht8km27.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_eph2hrn0m = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_eph2hrn0m.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_reload_fx81151hw = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_reload_fx81151hw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_reload = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted_ozhdg60sq = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted_ozhdg60sq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted_xrwz8a1qu = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted_xrwz8a1qu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_5zplp50dl = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_5zplp50dl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_ik75uw7ly = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_ik75uw7ly.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_b5wuheq22 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_b5wuheq22.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_ab4udnvvy = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_ab4udnvvy.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_d3cs9xosk = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_d3cs9xosk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_accountChanged_sb1n04qqw = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5534025912102772_accountChanged_sb1n04qqw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_accountChanged = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_accountChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_autocalcAmountChanged_wmi30x8hz = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5534025912102772_autocalcAmountChanged_wmi30x8hz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_autocalcAmountChanged = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_autocalcAmountChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6802360265496969_stage_azdj3pcjt = async (event, scope) => {
    return await actions.inst_evh_6802360265496969_stage_azdj3pcjt.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6802360265496969_stage = async (event, scope) => {
    return await actions.inst_evh_6802360265496969_stage.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6802360265496969_unstage_ueltguzbh = async (event, scope) => {
    return await actions.inst_evh_6802360265496969_unstage_ueltguzbh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6802360265496969_unstage = async (event, scope) => {
    return await actions.inst_evh_6802360265496969_unstage.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3083451129821185_recalculate_yvqdp52d3 = async (event, scope) => {
    return await actions.inst_evh_3083451129821185_recalculate_yvqdp52d3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3083451129821185_recalculate = async (event, scope) => {
    return await actions.inst_evh_3083451129821185_recalculate.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_success_mgpu74ha7 = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_success_mgpu74ha7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_success_9n9f0lebf = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_success_9n9f0lebf.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_success_jla2edxfx = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_success_jla2edxfx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_5bchkyrft = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_5bchkyrft.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7253299514750264_clickPreventStop_izm99m383 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7253299514750264_clickPreventStop_izm99m383.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7253299514750264_clickPreventStop_6w9qlxo6j = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7253299514750264_clickPreventStop_6w9qlxo6j.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7253299514750264_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_7253299514750264_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="Payment"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :color="$config.payment.color"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
        <UcPaymentHeader
          :payment="item"
          :config="config"
          @reload="evh_6466705138206980_reload($event, {})"
          @deleted="evh_6466705138206980_deleted($event, {})"
        >
        </UcPaymentHeader>
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        name="payment"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <FieldsetPaymentGeneral
          :config="config"
          :payment="item"
          :transactionTypes="transactionTypes"
          :formId="formId"
          :reload="lastLoaded"
          :autocalcAmount="autocalcAmount"
          @accountChanged="evh_5534025912102772_accountChanged($event, {})"
          @autocalcAmountChanged="
            evh_5534025912102772_autocalcAmountChanged($event, {})
          "
        >
        </FieldsetPaymentGeneral>
        <ItpRow>
          <ItpCol :w="12" form>
            <ItpFormGroup
              :label-cols-md="2"
              :content-cols-md="10"
              name="attachments"
              :label="gettext('Attachments')"
              labelAlign="right"
            >
              <UcAttachmentsPanel
                layout="wrap"
                :parent="item"
                :pending="pendingAttachments"
                :readonly="item.isReadonly"
                :reload="attachmentsReload"
                @stage="evh_6802360265496969_stage($event, {})"
                @unstage="evh_6802360265496969_unstage($event, {})"
              >
              </UcAttachmentsPanel>
            </ItpFormGroup>
          </ItpCol>
        </ItpRow>
      </ItpForm>
      <hr />
      <UcPaymentInvoices
        :payment="item"
        :reload="lastLoaded"
        @recalculate="evh_3083451129821185_recalculate($event, {})"
      >
      </UcPaymentInvoices>
    </UcBody>
    <template #footer>
      <UcFooter
        :card="!modal"
        :modal="modal"
        :color="$config.payment.color"
        v-if="loaded"
      >
        <ItpButton
          :text="pgettext('Button', 'Save changes')"
          :disabled="!forms.payment || !forms.payment.submittable"
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.save.isRunning"
          class="me-2"
          v-if="!isNew && !item.isReadonly"
        >
        </ItpButton>
        <ItpButton
          :text="pgettext('Command', 'Complete')"
          :disabled="!isNew && item.readonly"
          variant="success"
          icon="fa-check"
          class="me-2"
          v-if="!isNew && !item.isReadonly"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          :text="pgettext('Button', 'Create payment')"
          :disabled="!forms.payment || !forms.payment.submittable"
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.save.isRunning"
          class="me-2"
          v-if="isNew"
        >
        </ItpButton>
        <ItpButton
          :text="pgettext('Button', 'Create another payment')"
          :disabled="!forms.payment || !forms.payment.submittable"
          variant="light"
          icon="mdi:forward"
          :spinning="actions.save.isRunning"
          class="me-2"
          v-if="!isNew"
          @click.prevent.stop="
            evh_7253299514750264_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
